import React, { useState, useEffect, useCallback, useRef } from 'react'
import { useForm, SubmitHandler, FieldValues, Resolver } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import {
  Save,
  Building2,
  MapPin,
  Users,
  Settings,
  CheckCircle,
  AlertCircle,
  Calendar,
  Bot,
  Database,
  Activity,
  Clock,
  Hash,
  ExternalLink,
  ToggleLeft,
  ToggleRight,
  X,
  AlertTriangle
} from 'lucide-react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api'
import toast from 'react-hot-toast'
import { GooglePlacesAutocomplete, AddressDetails } from './GooglePlacesAutocomplete'
import { SearchableEntitySelect } from './SearchableEntitySelect'
import { ProjectLocationEditor } from './ProjectLocationEditor'
import type { ProjectLocation } from './ProjectLocationEditor'

// Typology options as specified by user
const TYPOLOGY_OPTIONS = [
  'Apartment',
  'House',
  'Apartment & House',
  'Tourist Apartment'
] as const

// Validation schema
const projectSchema = z.object({
  name: z.string()
    .min(1, 'Project name is required')
    .max(255, 'Project name must be less than 255 characters')
    .transform(val => val.trim())
    .refine(val => val.length > 0, 'Project name cannot be empty after trimming'),
  website: z.string()
    .min(1, 'Website is required')
    .url('Please enter a valid URL (e.g., https://example.com)')
    .refine((val) => val.startsWith('http://') || val.startsWith('https://'),
      'Website must start with http:// or https://'),
  address: z.string()
    .max(500, 'Address must be less than 500 characters')
    .transform(val => val?.trim() || '')
    .optional(),
  city: z.string()
    .max(100, 'City name is too long')
    .optional(),
  district: z.string()
    .max(100, 'District name is too long')
    .optional(),
  status: z.string().min(1, 'Please select a valid project status').max(100, 'Status is too long'),
  architect_id: z.union([
    z.number().int('Architect ID must be a whole number').positive('Architect ID must be positive'),
    z.null()
  ]).optional(),
  developer_id: z.union([
    z.number().int('Developer ID must be a whole number').positive('Developer ID must be positive'),
    z.null()
  ]).optional(),
  broker_id: z.union([
    z.number().int('Broker ID must be a whole number').positive('Broker ID must be positive'),
    z.null()
  ]).optional(),
  typology: z.string()
    .max(100, 'Typology name is too long')
    .optional(),
  full_description: z.string()
    .max(5000, 'Description must be less than 5000 characters')
    .nullable()
    .optional()
    .transform(val => val || ''),
  area: z.string()
    .max(100, 'Area name is too long')
    .optional(),
  neighbourhood: z.string()
    .max(100, 'Neighbourhood name is too long')
    .optional(),
  listing_date: z.string()
    .optional(),
  has_parking: z.boolean(),
  has_pool: z.boolean(),
  has_gym: z.boolean(),
  sold_out: z.boolean(),
  sold_out_date: z.string().optional(),
  latitude: z.preprocess((val) => {
    if (val === '' || val === null || val === undefined) return undefined
    if (typeof val === 'number') return val
    const num = Number(val)
    return Number.isFinite(num) ? num : undefined
  }, z.number().min(-90).max(90).optional()),
  longitude: z.preprocess((val) => {
    if (val === '' || val === null || val === undefined) return undefined
    if (typeof val === 'number') return val
    const num = Number(val)
    return Number.isFinite(num) ? num : undefined
  }, z.number().min(-180).max(180).optional()),
  scrap_status: z.enum(['successful', 'http_error', 'connection_error', 'other_error', 'pending']).nullable().optional(),
  scrap_error_message: z.string().max(500, 'Error message is too long').optional(),
  payment_terms: z.object({
    reservation: z.string().optional(),
    promissory: z.string().optional(),
    payment_count: z.string().optional(),
    payment_percentage: z.string().optional(),
    deed: z.string().optional(),
    comment: z.string().optional(),
  }).optional(),
})

type ProjectFormData = z.infer<typeof projectSchema>

type ProjectUpdatePayload = ProjectFormData & { force_units_sold?: boolean }

interface SoldOutValidationResult {
  projectId: number
  totalUnits: number
  soldUnits: number
  unsoldUnits: number
  unsoldBreakdown: Array<{ status: string; count: number }>
  isMarkedSoldOut: boolean
}

const STATUS_OPTIONS = [
  { value: 'active', label: 'Active' },
  { value: 'sold_out', label: 'Sold Out' },
  { value: 'pending', label: 'Pending' },
  { value: 'cancelled', label: 'Cancelled' },
  { value: 'completed', label: 'Completed' },
  { value: 'inactive', label: 'Inactive' },
]

interface ProjectEditModalProps {
  isOpen: boolean
  onClose: () => void
  projectId: number
  initialData?: Partial<ProjectFormData>
  onSuccess?: () => void
}

export function ProjectEditModal({ isOpen, onClose, projectId, initialData, onSuccess }: ProjectEditModalProps) {
  const [isLoading, setIsLoading] = useState(false)
  const [loadedProjectData, setLoadedProjectData] = useState<any>(null)
  const [pendingSubmission, setPendingSubmission] = useState<ProjectUpdatePayload | null>(null)
  const [showSoldOutConfirm, setShowSoldOutConfirm] = useState(false)
  const [soldOutValidation, setSoldOutValidation] = useState<SoldOutValidationResult | null>(null)
  const [isValidatingSoldOut, setIsValidatingSoldOut] = useState(false)
  const queryClient = useQueryClient()
  const lastSubmittedPayloadRef = useRef<ProjectUpdatePayload | null>(null)

  const {
    register,
    handleSubmit,
    formState: { errors, isDirty, isValid },
    reset,
    trigger,
    watch,
    setValue,
  } = useForm<ProjectFormData>({
    resolver: zodResolver(projectSchema) as Resolver<ProjectFormData>,
    defaultValues: {
      name: '',
      website: '',
      address: '',
      city: '',
      district: '',
      status: 'active',
      architect_id: undefined,
      developer_id: undefined,
      broker_id: undefined,
      typology: '',
      full_description: '',
      area: '',
      neighbourhood: '',
      listing_date: '',
      has_parking: false,
      has_pool: false,
      has_gym: false,
      sold_out: false,
      sold_out_date: '',
      latitude: undefined,
      longitude: undefined,
      scrap_status: undefined,
      scrap_error_message: '',
      payment_terms: {
        reservation: '',
        promissory: '',
        payment_count: '',
        payment_percentage: '',
        deed: '',
        comment: '',
      },
      ...initialData,
    },
    mode: 'onChange',
  })

  useEffect(() => {
    register('latitude')
    register('longitude')
  }, [register])

  // Watch form values for real-time feedback
  const watchedValues = watch()

  // Scraped and Discovery projects keep the broker's label ('Em Construção', 'Brevemente'):
  // list it, otherwise the select shows blank.
  const storedStatus: string | undefined = initialData?.status ?? loadedProjectData?.status
  const extraStatus = storedStatus && !STATUS_OPTIONS.some((option) => option.value === storedStatus) ? storedStatus : null

  const handleProjectLocationChange = useCallback((loc: ProjectLocation) => {
    setValue('latitude', loc.latitude, { shouldDirty: true, shouldValidate: true })
    setValue('longitude', loc.longitude, { shouldDirty: true, shouldValidate: true })
    setValue('address', loc.address, { shouldDirty: true, shouldValidate: true })
    setValue('district', loc.district, { shouldDirty: true, shouldValidate: true })
    setValue('city', loc.city, { shouldDirty: true, shouldValidate: true })
  }, [setValue])

  // Helper function to get input field class names
  const getInputClassName = (fieldName: keyof ProjectFormData, hasError: boolean = false) => {
    const baseClass = "form-input"
    if (hasError) return `${baseClass} border-danger-300 focus:border-danger-500 focus:ring-danger-500`
    if (isDirty && !hasError) return `${baseClass} border-success-300 focus:border-success-500 focus:ring-success-500`
    return baseClass
  }

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: async (data: ProjectUpdatePayload) => {
      const response = await api.put(`/projects/${projectId}`, data)
      return response.data
    },
    onSuccess: (data) => {
      toast.success('Project updated successfully!')
      queryClient.invalidateQueries({ queryKey: ['projects'] })
      queryClient.invalidateQueries({ queryKey: ['project', projectId] })
      setPendingSubmission(null)
      setShowSoldOutConfirm(false)
      setSoldOutValidation(null)
      setIsValidatingSoldOut(false)
      lastSubmittedPayloadRef.current = null
      onClose()
      onSuccess?.()
    },
    onError: (error: any) => {
      const errorCode = error.response?.data?.code

      if (errorCode === 'UNSOLD_UNITS_PRESENT') {
        const unsoldUnits = error.response?.data?.unsoldUnits ?? 0
        const totalUnits = error.response?.data?.totalUnits ?? 0
        const breakdown = error.response?.data?.breakdown ?? []

        setSoldOutValidation({
          projectId,
          totalUnits,
          soldUnits: totalUnits - unsoldUnits,
          unsoldUnits,
          unsoldBreakdown: breakdown,
          isMarkedSoldOut: false,
        })

        setPendingSubmission(lastSubmittedPayloadRef.current)
        setShowSoldOutConfirm(true)

        toast(
          <div className="flex flex-col">
            <div className="font-semibold text-secondary-900">Confirmation required</div>
            <div className="text-sm text-secondary-700 mt-1">
              Some units are still marked as Available or Reserved. Please confirm if you want to mark them as Sold.
            </div>
          </div>,
          {
            icon: '⚠️',
            duration: 6000,
          },
        )
        return
      }

      console.error('Error updating project:', error)
      console.error('Error response:', error.response)
      console.error('Error data:', error.response?.data)
      console.error('Error status:', error.response?.status)
      
      // Extract detailed error information
      let errorMessage = 'Failed to update project'
      let errorDetails = ''
      
      if (error.response?.data) {
        if (error.response.data.message) {
          errorMessage = error.response.data.message
        }
        
        // Handle validation errors
        if (error.response.data.message && Array.isArray(error.response.data.message)) {
          const validationErrors = error.response.data.message
          errorDetails = validationErrors.map((err: any) => {
            if (err.constraints) {
              return Object.values(err.constraints).join(', ')
            }
            return err.message || err
          }).join('\n')
        } else if (typeof error.response.data.message === 'string') {
          errorDetails = error.response.data.message
        }
      }
      
      // Show detailed error to user
      toast.error(
        <div>
          <div className="font-semibold">{errorMessage}</div>
          {errorDetails && (
            <div className="text-sm mt-1 whitespace-pre-line">{errorDetails}</div>
          )}
        </div>,
        {
          duration: 8000,
          style: {
            background: '#fef2f2',
            border: '1px solid #dc2626',
            color: '#991b1b',
            maxWidth: '500px',
          },
          icon: '🚨',
        }
      )
    },
  })

  const submitUpdate = useCallback((payload: ProjectUpdatePayload) => {
    lastSubmittedPayloadRef.current = payload
    updateMutation.mutate(payload)
  }, [updateMutation])

  const validateSoldOutTransition = useCallback(async (payload: ProjectUpdatePayload) => {
    if (!projectId) {
      return
    }

    setIsValidatingSoldOut(true)
    const toastId = toast.loading('Validating units before marking as Sold Out...')

    try {
      const response = await api.get(`/projects/${projectId}/sold-out-validation`)
      const validation = response.data as SoldOutValidationResult

      if (validation.unsoldUnits > 0) {
        setPendingSubmission(payload)
        setSoldOutValidation(validation)
        setShowSoldOutConfirm(true)
        return
      }

      submitUpdate(payload)
    } catch (validationError) {
      console.error('Sold-out validation failed:', validationError)
      toast.error('Unable to validate project units. Please try again.')
    } finally {
      toast.dismiss(toastId)
      setIsValidatingSoldOut(false)
    }
  }, [projectId, submitUpdate])

  const handleConfirmSoldOut = useCallback(() => {
    if (!pendingSubmission) {
      return
    }

    const payload = {
      ...pendingSubmission,
      force_units_sold: true,
    }

    setShowSoldOutConfirm(false)
    setPendingSubmission(null)
    submitUpdate(payload)
  }, [pendingSubmission, submitUpdate])

  const handleCancelSoldOut = useCallback(() => {
    setShowSoldOutConfirm(false)
    setPendingSubmission(null)
  }, [])

  // Clean form data before submission
  const cleanFormData = useCallback((data: ProjectFormData) => {
    console.log('=== CLEANING FORM DATA ===')
    console.log('Input data:', data)
    
    const cleaned = { ...data }
    
    // Log each field before cleaning
    console.log('Before cleaning:')
    console.log('architect_id:', cleaned.architect_id, 'type:', typeof cleaned.architect_id)
    console.log('developer_id:', cleaned.developer_id, 'type:', typeof cleaned.developer_id)
    console.log('broker_id:', cleaned.broker_id, 'type:', typeof cleaned.broker_id)
    
    // Foreign key fields that should preserve null values
    const foreignKeyFields = ['architect_id', 'developer_id', 'broker_id']
    
    Object.keys(cleaned).forEach(key => {
      const isForeignKeyField = foreignKeyFields.includes(key)
      
      if (cleaned[key] === undefined || cleaned[key] === '') {
        console.log(`Deleting ${key} because it's ${cleaned[key]}`)
        delete cleaned[key]
      } else if (cleaned[key] === null && !isForeignKeyField) {
        console.log(`Deleting ${key} because it's null and not a foreign key field`)
        delete cleaned[key]
      } else if (cleaned[key] === null && isForeignKeyField) {
        console.log(`Preserving ${key} as null (foreign key field)`)
        // Keep null values for foreign key fields
      }
    })

    if (cleaned.latitude !== undefined) {
      const parsedLat = typeof cleaned.latitude === 'string' ? parseFloat(cleaned.latitude) : cleaned.latitude
      if (Number.isFinite(parsedLat)) {
        cleaned.latitude = parsedLat
      } else {
        delete cleaned.latitude
      }
    }

    if (cleaned.longitude !== undefined) {
      const parsedLng = typeof cleaned.longitude === 'string' ? parseFloat(cleaned.longitude) : cleaned.longitude
      if (Number.isFinite(parsedLng)) {
        cleaned.longitude = parsedLng
      } else {
        delete cleaned.longitude
      }
    }
    
    console.log('After cleaning:')
    console.log('architect_id:', cleaned.architect_id, 'type:', typeof cleaned.architect_id)
    console.log('developer_id:', cleaned.developer_id, 'type:', typeof cleaned.developer_id)
    console.log('broker_id:', cleaned.broker_id, 'type:', typeof cleaned.broker_id)
    console.log('Final cleaned data:', cleaned)
    console.log('Keys in cleaned data:', Object.keys(cleaned))

    // Clean payment_terms: strip empty strings, send null if all empty (to clear in DB)
    if (cleaned.payment_terms) {
      const pt = { ...cleaned.payment_terms }
      Object.keys(pt).forEach(k => {
        if (!pt[k] || pt[k].trim() === '') delete pt[k]
      })
      if (Object.keys(pt).length === 0) {
        cleaned.payment_terms = null as any
      } else {
        cleaned.payment_terms = pt
      }
    }

    return cleaned
  }, [])

  const onSubmit = useCallback<SubmitHandler<ProjectFormData>>((data) => {
    console.log('=== FORM SUBMISSION DEBUG ===')
    console.log('Original form data:', data)
    console.log('Form is valid:', isValid)
    console.log('Form errors:', errors)
    console.log('Form is dirty:', isDirty)
    console.log('Mutation pending:', updateMutation.isPending)
    
    // Manual validation check
    try {
      const validatedData = projectSchema.parse(data)
      console.log('Schema validation passed:', validatedData)
    } catch (validationError) {
      console.error('Schema validation failed:', validationError)
      toast.error('Form validation failed. Please check your input.', {
        duration: 5000,
        style: {
          background: '#fef2f2',
          border: '1px solid #dc2626',
          color: '#991b1b',
        },
        icon: '⚠️',
      })
      return
    }
    
    // Check for required fields
    const requiredFields = ['name', 'website', 'status']
    const missingFields = requiredFields.filter(field => !data[field as keyof ProjectFormData])
    
    if (missingFields.length > 0) {
      toast.error(`Please fill in the required fields: ${missingFields.join(', ')}`, {
        duration: 5000,
        style: {
          background: '#fef2f2',
          border: '1px solid #dc2626',
          color: '#991b1b',
        },
        icon: '⚠️',
      })
      return
    }
    
    const cleanedData = cleanFormData(data) as ProjectUpdatePayload
    console.log('Cleaned form data:', cleanedData)
    console.log('Keys in cleaned data:', Object.keys(cleanedData))
    console.log('Architect ID in cleaned data:', cleanedData.architect_id)
    console.log('Developer ID in cleaned data:', cleanedData.developer_id)
    console.log('Broker ID in cleaned data:', cleanedData.broker_id)
    
    const originalProject = loadedProjectData ?? initialData
    const originalStatus = typeof originalProject?.status === 'string'
      ? originalProject.status.toLowerCase()
      : originalProject?.status
    const wasAlreadySoldOut = Boolean(
      originalProject && (
        originalStatus === 'sold_out' ||
        originalProject?.sold_out === true
      )
    )

    const nextStatus = typeof cleanedData.status === 'string'
      ? cleanedData.status.toLowerCase()
      : cleanedData.status
    const willBeSoldOut = nextStatus === 'sold_out' || cleanedData.sold_out === true

    if (willBeSoldOut && !wasAlreadySoldOut) {
      validateSoldOutTransition(cleanedData)
      return
    }

    submitUpdate(cleanedData)
  }, [isValid, errors, isDirty, cleanFormData, loadedProjectData, initialData, validateSoldOutTransition, submitUpdate])

  const handleLocationSave = useCallback(() => {
    handleSubmit(onSubmit as SubmitHandler<FieldValues>)()
  }, [handleSubmit, onSubmit])

  // Debug form state changes
  useEffect(() => {
    console.log('=== useEffect TRIGGERED ===')
    console.log('=== FORM STATE DEBUG ===')
    console.log('Form is valid:', isValid)
    console.log('Form is dirty:', isDirty)
    console.log('Form errors:', errors)
    console.log('Current form values:', watchedValues)
    console.log('Mutation pending:', updateMutation.isPending)
    
    // Always run manual validation for debugging
    console.log('=== MANUAL VALIDATION DEBUG ===')
    console.log('About to run schema validation...')
    console.log('architect_id type:', typeof watchedValues.architect_id, 'value:', watchedValues.architect_id)
    console.log('developer_id type:', typeof watchedValues.developer_id, 'value:', watchedValues.developer_id)
    console.log('broker_id type:', typeof watchedValues.broker_id, 'value:', watchedValues.broker_id)
    console.log('full_description length:', watchedValues.full_description?.length || 0)
    console.log('full_description type:', typeof watchedValues.full_description)
    console.log('full_description first 100 chars:', watchedValues.full_description?.substring(0, 100))
    
    // Test simple validation first
    console.log('Testing simple validation...')
    try {
      const simpleTest = z.string().min(1).safeParse(watchedValues.name)
      console.log('Simple name validation:', simpleTest)
    } catch (e) {
      console.error('Simple validation error:', e)
    }
    
    // Test description validation specifically
    console.log('Testing description validation...')
    console.log('Description length:', watchedValues.full_description?.length || 0)
    console.log('Description is empty:', !watchedValues.full_description || watchedValues.full_description.length === 0)
    
    try {
      const descTest = z.string().max(5000).optional().safeParse(watchedValues.full_description)
      console.log('Description validation:', descTest)
      if (!descTest.success) {
        console.log('Description validation errors:', descTest.error.issues)
        descTest.error.issues.forEach(issue => {
          console.log(`Description error: ${issue.message}`)
          console.log(`Path: ${issue.path.join('.')}`)
          console.log(`Code: ${issue.code}`)
        })
      } else {
        console.log('Description validation PASSED')
      }
    } catch (e) {
      console.error('Description validation error:', e)
    }
    
    // Test with a simple description
    try {
      const simpleDesc = "Test description"
      const simpleDescTest = z.string().max(5000).optional().safeParse(simpleDesc)
      console.log('Simple description validation:', simpleDescTest)
    } catch (e) {
      console.error('Simple description validation error:', e)
    }
    
    try {
      const result = projectSchema.safeParse(watchedValues)
      console.log('Schema validation result:', result)
      if (!result.success) {
        console.log('Validation errors found:', result.error.issues)
        result.error.issues.forEach(issue => {
          console.log(`- Field "${issue.path.join('.')}": ${issue.message}`)
          console.log(`  Value: ${JSON.stringify(watchedValues[issue.path[0] as keyof typeof watchedValues])}`)
          console.log(`  Type: ${typeof watchedValues[issue.path[0] as keyof typeof watchedValues]}`)
        })
      } else {
        console.log('Schema validation passed - form should be valid')
      }
    } catch (error) {
      console.error('Error during manual validation:', error)
      console.error('Error details:', error)
    }
    
    // Log specific validation issues
    if (!isValid && Object.keys(errors).length === 0) {
      console.warn('Form is invalid but no errors shown - this might be a validation schema issue')
      console.log('Checking individual field validation...')
      
      // Test each field against the schema manually
      try {
        const result = projectSchema.safeParse(watchedValues)
        console.log('Manual schema validation result:', result)
        if (!result.success) {
          console.log('Schema validation errors:', result.error.issues)
          result.error.issues.forEach(issue => {
            console.log(`Field "${issue.path.join('.')}": ${issue.message}`)
            console.log(`  Value: ${JSON.stringify(watchedValues[issue.path[0] as keyof typeof watchedValues])}`)
            console.log(`  Type: ${typeof watchedValues[issue.path[0] as keyof typeof watchedValues]}`)
          })
        } else {
          console.log('Manual validation passed - form should be valid')
        }
      } catch (error) {
        console.error('Error during manual validation:', error)
      }
    }
    
    // Test each required field
    const requiredFields = ['name', 'website', 'status']
    requiredFields.forEach(field => {
      const value = watchedValues[field as keyof typeof watchedValues]
      console.log(`${field}:`, value, 'type:', typeof value, 'length:', typeof value === 'string' ? value.length : 'N/A')
    })
  }, [isValid, isDirty, errors, watchedValues, updateMutation.isPending])

  // Load project data if not provided
  useEffect(() => {
    if (projectId && !initialData) {
      const loadProject = async () => {
        try {
          console.log('Loading project data for ID:', projectId)
          setIsLoading(true)
          const response = await api.get(`/projects/${projectId}`)
          const projectData = response.data
          
          console.log('Loaded project data:', projectData)
          console.log('architect_id from backend:', projectData.architect_id, 'type:', typeof projectData.architect_id)
          console.log('developer_id from backend:', projectData.developer_id, 'type:', typeof projectData.developer_id)
          console.log('broker_id from backend:', projectData.broker_id, 'type:', typeof projectData.broker_id)
          setLoadedProjectData(projectData)
          
          // Reset form with loaded data
          const formData = {
            name: projectData.name || '',
            website: projectData.website || '',
            address: projectData.address || '',
            city: projectData.city || '',
            district: projectData.district || '',
            status: projectData.status || 'active',
            architect_id: projectData.architect_id ? projectData.architect_id : undefined,
            developer_id: projectData.developer_id ? projectData.developer_id : undefined,
            broker_id: projectData.broker_id ? projectData.broker_id : undefined,
            typology: projectData.typology || '',
            full_description: projectData.full_description || '',
            area: projectData.area || '',
            neighbourhood: projectData.neighbourhood || '',
            listing_date: projectData.listing_date ? new Date(projectData.listing_date).toISOString().split('T')[0] : '',
            has_parking: projectData.has_parking || false,
            has_pool: projectData.has_pool || false,
            has_gym: projectData.has_gym || false,
            sold_out: projectData.sold_out || false,
            sold_out_date: projectData.sold_out_date ? new Date(projectData.sold_out_date).toISOString().split('T')[0] : '',
            latitude: projectData.latitude,
            longitude: projectData.longitude,
            scrap_status: projectData.scrap_status,
            scrap_error_message: projectData.scrap_error_message || '',
          }
          
          console.log('Form data after processing:')
          console.log('architect_id:', formData.architect_id, 'type:', typeof formData.architect_id)
          console.log('developer_id:', formData.developer_id, 'type:', typeof formData.developer_id)
          console.log('broker_id:', formData.broker_id, 'type:', typeof formData.broker_id)
          console.log('Resetting form with data:', formData)
          reset(formData)
          trigger()
        } catch (error) {
          console.error('Error loading project:', error)
          toast.error('Failed to load project data')
        } finally {
          setIsLoading(false)
        }
      }
      loadProject()
    } else if (initialData) {
      console.log('Using provided initialData:', initialData)
      console.log('Processing initialData for null values...')
      
      // Process initialData to convert null values to undefined
      const processedInitialData = {
        ...initialData,
        architect_id: initialData.architect_id ? initialData.architect_id : undefined,
        developer_id: initialData.developer_id ? initialData.developer_id : undefined,
        broker_id: initialData.broker_id ? initialData.broker_id : undefined,
      }
      
      console.log('Processed initialData:', processedInitialData)
      console.log('architect_id:', processedInitialData.architect_id, 'type:', typeof processedInitialData.architect_id)
      console.log('developer_id:', processedInitialData.developer_id, 'type:', typeof processedInitialData.developer_id)
      console.log('broker_id:', processedInitialData.broker_id, 'type:', typeof processedInitialData.broker_id)
      
      reset(processedInitialData)
      trigger()
    }
  }, [projectId, initialData, reset, trigger])

  useEffect(() => {
    if (!isOpen) {
      setShowSoldOutConfirm(false)
      setPendingSubmission(null)
      setSoldOutValidation(null)
      setIsValidatingSoldOut(false)
      lastSubmittedPayloadRef.current = null
    }
  }, [isOpen])

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black bg-opacity-50 transition-opacity" onClick={onClose}></div>
      
      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative bg-white rounded-xl shadow-tagus-lg w-full max-w-4xl max-h-[90vh] overflow-hidden">
          {/* Header */}
          <div className="border-b border-secondary-200 px-6 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center">
                  <Building2 className="h-6 w-6 text-primary-600" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-secondary-900">Edit Project</h2>
                  <p className="text-secondary-500 text-sm">Update project information</p>
                </div>
              </div>
              <button
                onClick={onClose}
                className="text-secondary-400 hover:text-secondary-600 transition-colors"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
          </div>

          {/* Form Content */}
          <div className="p-6 max-h-[calc(90vh-120px)] overflow-y-auto">
            {isLoading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
              </div>
            ) : (
              <form onSubmit={handleSubmit(onSubmit as SubmitHandler<FieldValues>)} className="space-y-6">
                {/* Basic Information */}
                <div key="basic-information" className="space-y-6">
                  <div className="flex items-center space-x-2 mb-4">
                    <Building2 className="h-5 w-5 text-primary-600" />
                    <h3 className="text-lg font-semibold text-secondary-900">Project Details</h3>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="form-label">
                        Project Name <span className="text-danger-500">*</span>
                      </label>
                      <input
                        {...register('name')}
                        type="text"
                        className={getInputClassName('name', !!errors.name)}
                        placeholder="Enter project name"
                      />
                      {errors.name && (
                        <p className="mt-1 text-sm text-danger-600">{errors.name.message}</p>
                      )}
                    </div>

                    <div>
                      <label className="form-label">
                        Website <span className="text-danger-500">*</span>
                      </label>
                      <input
                        {...register('website')}
                        type="url"
                        className={getInputClassName('website', !!errors.website)}
                        placeholder="https://example.com"
                      />
                      {errors.website && (
                        <p className="mt-1 text-sm text-danger-600">{errors.website.message}</p>
                      )}
                    </div>

                    <div>
                      <label className="form-label">Address</label>
                      <GooglePlacesAutocomplete
                        value={watchedValues.address || ''}
                        onChange={(value) => setValue('address', value, { shouldDirty: true })}
                        onAddressSelect={(details: AddressDetails) => {
                          const resolvedAddress = details.formattedAddress || details.address || watchedValues.address || ''
                          // Google's labels are English here ('Faro District'): they only fill an empty field.
                          const resolvedCity = watchedValues.city || details.city || ''
                          const resolvedDistrict = watchedValues.district || details.district || ''

                          if (resolvedAddress !== watchedValues.address) {
                            setValue('address', resolvedAddress, { shouldDirty: true, shouldValidate: true })
                          }
                          if (resolvedCity !== watchedValues.city) {
                            setValue('city', resolvedCity, { shouldDirty: true, shouldValidate: true })
                          }
                          if (resolvedDistrict !== watchedValues.district) {
                            setValue('district', resolvedDistrict, { shouldDirty: true, shouldValidate: true })
                          }

                          if (typeof details.latitude === 'number' && typeof details.longitude === 'number') {
                            handleProjectLocationChange({
                              latitude: details.latitude,
                              longitude: details.longitude,
                              address: resolvedAddress,
                              district: resolvedDistrict,
                              city: resolvedCity,
                            })
                          }
                        }}
                        placeholder="Start typing address (e.g., Rua de Belém 1, Lisbon)..."
                        error={errors.address?.message}
                      />
                    </div>

                    <div>
                      <label className="form-label">City</label>
                      <input
                        {...register('city')}
                        type="text"
                        className={getInputClassName('city', !!errors.city)}
                        placeholder="Enter city"
                      />
                      {errors.city && (
                        <p className="mt-1 text-sm text-danger-600">{errors.city.message}</p>
                      )}
                    </div>

                    <div>
                      <label className="form-label">District</label>
                      <input
                        {...register('district')}
                        type="text"
                        className={getInputClassName('district', !!errors.district)}
                        placeholder="Enter district"
                      />
                      {errors.district && (
                        <p className="mt-1 text-sm text-danger-600">{errors.district.message}</p>
                      )}
                    </div>

                    <div>
                      <label className="form-label">
                        Status <span className="text-danger-500">*</span>
                      </label>
                      <select
                        {...register('status')}
                        className={getInputClassName('status', !!errors.status)}
                        defaultValue="active"
                      >
                        {extraStatus && <option value={extraStatus}>{extraStatus}</option>}
                        {STATUS_OPTIONS.map((option) => (
                          <option key={option.value} value={option.value}>{option.label}</option>
                        ))}
                      </select>
                      {errors.status && (
                        <p className="mt-1 text-sm text-danger-600">{errors.status.message}</p>
                      )}
                    </div>

                    <div>
                      <label className="form-label">Typology</label>
                      <select
                        {...register('typology')}
                        className={getInputClassName('typology', !!errors.typology)}
                      >
                        <option value="">Select typology</option>
                        {TYPOLOGY_OPTIONS.map((option) => (
                          <option key={option} value={option}>{option}</option>
                        ))}
                      </select>
                      {errors.typology && (
                        <p className="mt-1 text-sm text-danger-600">{errors.typology.message}</p>
                      )}
                    </div>

                    <div>
                      <label className="form-label">Area</label>
                      <input
                        {...register('area')}
                        type="text"
                        className={getInputClassName('area', !!errors.area)}
                        placeholder="Enter area"
                      />
                      {errors.area && (
                        <p className="mt-1 text-sm text-danger-600">{errors.area.message}</p>
                      )}
                    </div>

                    <div>
                      <label className="form-label">Neighbourhood</label>
                      <input
                        {...register('neighbourhood')}
                        type="text"
                        className={getInputClassName('neighbourhood', !!errors.neighbourhood)}
                        placeholder="Enter neighbourhood"
                      />
                      {errors.neighbourhood && (
                        <p className="mt-1 text-sm text-danger-600">{errors.neighbourhood.message}</p>
                      )}
                    </div>

                    <div>
                      <label className="form-label">Launch Date</label>
                      <input
                        {...register('listing_date')}
                        type="date"
                        className={getInputClassName('listing_date', !!errors.listing_date)}
                      />
                      {errors.listing_date && (
                        <p className="mt-1 text-sm text-danger-600">{errors.listing_date.message}</p>
                      )}
                    </div>

                    <div>
                      <label className="form-label">Sales Date</label>
                      <input
                        {...register('sold_out_date')}
                        type="date"
                        className={getInputClassName('sold_out_date', !!errors.sold_out_date)}
                      />
                      {errors.sold_out_date && (
                        <p className="mt-1 text-sm text-danger-600">{errors.sold_out_date.message}</p>
                      )}
                    </div>
                  </div>

                <div className="space-y-4">
                  <div className="flex items-center space-x-2">
                    <MapPin className="h-5 w-5 text-primary-600" />
                    <h3 className="text-lg font-semibold text-secondary-900">Location</h3>
                  </div>
                  <ProjectLocationEditor
                    latitude={watchedValues.latitude ? Number(watchedValues.latitude) : null}
                    longitude={watchedValues.longitude ? Number(watchedValues.longitude) : null}
                    address={watchedValues.address || ''}
                    district={watchedValues.district || ''}
                    city={watchedValues.city || ''}
                    onLocationChange={handleProjectLocationChange}
                    onSave={handleLocationSave}
                  />
                </div>

                  <div>
                    <label className="form-label">Description</label>
                    <textarea
                      {...register('full_description')}
                      rows={4}
                      className={getInputClassName('full_description', !!errors.full_description)}
                      placeholder="Enter project description"
                    />
                    {errors.full_description && (
                      <p className="mt-1 text-sm text-danger-600">{errors.full_description.message}</p>
                    )}
                  </div>
                </div>

                {/* Associated Entities */}
                <div key="associated-entities" className="space-y-6">
                  <div className="flex items-center space-x-2 mb-4">
                    <Users className="h-5 w-5 text-primary-600" />
                    <h3 className="text-lg font-semibold text-secondary-900">Associated Entities</h3>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <SearchableEntitySelect
                      key="architect-select"
                      label="Architect"
                      value={watchedValues.architect_id}
                      onChange={(value) => {
                        console.log('Architect onChange:', value)
                        if (value === undefined || value === null) {
                          setValue('architect_id', null, { shouldDirty: true, shouldValidate: true })
                        } else {
                          setValue('architect_id', value, { shouldDirty: true, shouldValidate: true })
                        }
                      }}
                      fetchUrl="/architects"
                      placeholder="Search architects..."
                      entityType="architect"
                    />

                    <SearchableEntitySelect
                      key="developer-select"
                      label="Developer"
                      value={watchedValues.developer_id}
                      onChange={(value) => {
                        console.log('Developer onChange:', value)
                        if (value === undefined || value === null) {
                          setValue('developer_id', null, { shouldDirty: true, shouldValidate: true })
                        } else {
                          setValue('developer_id', value, { shouldDirty: true, shouldValidate: true })
                        }
                      }}
                      fetchUrl="/developers"
                      placeholder="Search developers..."
                      entityType="developer"
                    />

                    <SearchableEntitySelect
                      key="broker-select"
                      label="Broker"
                      value={watchedValues.broker_id}
                      onChange={(value) => {
                        console.log('Broker onChange:', value)
                        if (value === undefined || value === null) {
                          setValue('broker_id', null, { shouldDirty: true, shouldValidate: true })
                        } else {
                          setValue('broker_id', value, { shouldDirty: true, shouldValidate: true })
                        }
                      }}
                      fetchUrl="/brokers"
                      placeholder="Search brokers..."
                      entityType="broker"
                    />
                  </div>
                </div>


                {/* Payment Terms */}
                <div key="payment-terms" className="space-y-6">
                  <div className="flex items-center space-x-2 mb-4">
                    <Activity className="h-5 w-5 text-primary-600" />
                    <h3 className="text-lg font-semibold text-secondary-900">Payment Terms</h3>
                  </div>

                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    <div>
                      <label className="form-label">Reservation</label>
                      <input
                        {...register('payment_terms.reservation')}
                        className="form-input"
                        placeholder="e.g. 5 000 €"
                      />
                    </div>
                    <div>
                      <label className="form-label">Promissory (CPCV)</label>
                      <input
                        {...register('payment_terms.promissory')}
                        className="form-input"
                        placeholder="e.g. 15%"
                      />
                    </div>
                    <div>
                      <label className="form-label">Nr. of Payments</label>
                      <input
                        {...register('payment_terms.payment_count')}
                        className="form-input"
                        placeholder="e.g. 5"
                      />
                    </div>
                    <div>
                      <label className="form-label">Percentage</label>
                      <input
                        {...register('payment_terms.payment_percentage')}
                        className="form-input"
                        placeholder="e.g. 10% each"
                      />
                    </div>
                    <div>
                      <label className="form-label">Deed (Escritura)</label>
                      <input
                        {...register('payment_terms.deed')}
                        className="form-input"
                        placeholder="e.g. 65%"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="form-label">Comment</label>
                    <textarea
                      {...register('payment_terms.comment')}
                      rows={2}
                      className="form-input"
                      placeholder="e.g. Discounts available for increased deposit"
                    />
                  </div>
                </div>

                {/* Features */}
                <div key="features" className="space-y-6">
                  <div className="flex items-center space-x-2 mb-4">
                    <CheckCircle className="h-5 w-5 text-primary-600" />
                    <h3 className="text-lg font-semibold text-secondary-900">Features</h3>
                  </div>
                  
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                    <div className="flex items-center space-x-3">
                      <input
                        {...register('has_parking')}
                        type="checkbox"
                        className="form-checkbox"
                      />
                      <label className="text-sm font-medium text-secondary-700">Parking</label>
                    </div>

                    <div className="flex items-center space-x-3">
                      <input
                        {...register('has_pool')}
                        type="checkbox"
                        className="form-checkbox"
                      />
                      <label className="text-sm font-medium text-secondary-700">Pool</label>
                    </div>

                    <div className="flex items-center space-x-3">
                      <input
                        {...register('has_gym')}
                        type="checkbox"
                        className="form-checkbox"
                      />
                      <label className="text-sm font-medium text-secondary-700">Gym</label>
                    </div>

                    <div className="flex items-center space-x-3">
                      <input
                        {...register('sold_out')}
                        type="checkbox"
                        className="form-checkbox"
                      />
                      <label className="text-sm font-medium text-secondary-700">Sold Out</label>
                    </div>
                  </div>
                </div>

                {/* Scraper Status */}
                <div key="scraper-status" className="space-y-6">
                  <div className="flex items-center space-x-2 mb-4">
                    <Bot className="h-5 w-5 text-primary-600" />
                    <h3 className="text-lg font-semibold text-secondary-900">Scraper Status</h3>
                  </div>
                  
                  <div className="grid grid-cols-1 gap-6">
                    <div>
                      <label className="form-label">
                        Scrap Status
                      </label>
                      <select
                        {...register('scrap_status')}
                        className={getInputClassName('scrap_status', !!errors.scrap_status)}
                      >
                        <option value="">Select status</option>
                        <option value="successful">Successful</option>
                        <option value="http_error">HTTP Error</option>
                        <option value="connection_error">Connection Error</option>
                        <option value="other_error">Other Error</option>
                        <option value="pending">Pending</option>
                      </select>
                      {errors.scrap_status && (
                        <p className="mt-1 text-sm text-danger-600">{errors.scrap_status.message}</p>
                      )}
                    </div>

                    {watchedValues.scrap_status && watchedValues.scrap_status !== 'successful' && (
                      <div>
                        <label className="form-label flex items-center space-x-2">
                          <AlertTriangle className="h-4 w-4 text-warning-600" />
                          <span>Scrap Error Message</span>
                        </label>
                        <textarea
                          {...register('scrap_error_message')}
                          rows={3}
                          className={getInputClassName('scrap_error_message', !!errors.scrap_error_message)}
                          placeholder="Enter error message (optional)"
                        />
                        {errors.scrap_error_message && (
                          <p className="mt-1 text-sm text-danger-600">{errors.scrap_error_message.message}</p>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                {/* Form Actions */}
                <div className="flex items-center justify-end space-x-3 pt-6 border-t border-secondary-200">
                  <button
                    type="button"
                    onClick={onClose}
                    className="btn-secondary"
                  >
                    Cancel
                  </button>
                  {!isValid && Object.keys(errors).length > 0 && (
                    <p className="text-sm text-danger-600 self-center">Fix the fields marked in red to save.</p>
                  )}
                  <button
                    type="submit"
                    disabled={updateMutation.isPending || !isValid || isValidatingSoldOut}
                    className="btn-primary flex items-center space-x-2"
                  >
                    <Save className="h-4 w-4" />
                    <span>
                      {updateMutation.isPending
                        ? 'Saving...'
                        : isValidatingSoldOut
                          ? 'Validating...'
                          : 'Save Changes'}
                    </span>
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
        {showSoldOutConfirm && (
          <div className="absolute inset-0 bg-black/40 flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full p-6 space-y-4">
              <div className="flex items-start space-x-3">
                <div className="w-12 h-12 rounded-full bg-warning-100 flex items-center justify-center">
                  <AlertTriangle className="h-6 w-6 text-warning-600" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-secondary-900">Mark all units as Sold?</h3>
                  <p className="text-sm text-secondary-600 mt-1">
                    This project still has units that are Available or Reserved.
                  </p>
                </div>
              </div>

              <p className="text-secondary-700 leading-relaxed">
                "Not all units in this project are currently marked as Sold. If you continue, all remaining
                Available/Reserved units will be automatically updated to Sold with today's Sales Date. Do you want to
                proceed?"
              </p>

              {soldOutValidation && (
                <div className="bg-secondary-50 border border-secondary-200 rounded-xl p-4 space-y-2">
                  <div className="flex justify-between text-sm text-secondary-700">
                    <span>Total units</span>
                    <span className="font-semibold text-secondary-900">{soldOutValidation.totalUnits}</span>
                  </div>
                  <div className="flex justify-between text-sm text-secondary-700">
                    <span>Units already Sold</span>
                    <span className="font-semibold text-secondary-900">{soldOutValidation.soldUnits}</span>
                  </div>
                  <div className="flex justify-between text-sm text-secondary-700">
                    <span>Units to update</span>
                    <span className="font-semibold text-secondary-900">{soldOutValidation.unsoldUnits}</span>
                  </div>

                  {soldOutValidation.unsoldBreakdown.length > 0 && (
                    <div className="pt-2 border-t border-secondary-200">
                      <p className="text-xs font-semibold text-secondary-500 uppercase tracking-wide mb-1">Breakdown</p>
                      <div className="space-y-1">
                        {soldOutValidation.unsoldBreakdown.map((item) => (
                          <div key={`${item.status}-${item.count}`} className="flex justify-between text-sm text-secondary-600">
                            <span className="capitalize">
                              {item.status.replace(/_/g, ' ') || 'Unknown'}
                            </span>
                            <span className="font-medium text-secondary-900">{item.count}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              <div className="flex justify-end space-x-3 pt-2">
                <button
                  type="button"
                  onClick={handleCancelSoldOut}
                  className="btn-secondary"
                  disabled={updateMutation.isPending}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleConfirmSoldOut}
                  className="btn-primary"
                  disabled={updateMutation.isPending}
                >
                  {updateMutation.isPending ? 'Updating...' : 'Confirm & Update Units'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
